import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import { Popover } from '@headlessui/react';
import { 
  BuildingOffice2Icon, BriefcaseIcon, DocumentTextIcon, 
  UserIcon, QuestionMarkCircleIcon, ChevronLeftIcon, 
  ChevronRightIcon, CurrencyDollarIcon, UsersIcon,
  CubeIcon, ShieldExclamationIcon, EnvelopeIcon,
  PhoneIcon, MapPinIcon, GlobeAltIcon, LockClosedIcon,
  ExclamationTriangleIcon, CheckCircleIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-hot-toast';

// Animation variants for page transitions
const pageVariants = {
  initial: {
    x: '100%',
    opacity: 0
  },
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30
    }
  },
  exit: {
    x: '-100%',
    opacity: 0
  }
};

// Tooltip animation variants
const tooltipVariants = {
  initial: { scale: 0.9, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  exit: { scale: 0.9, opacity: 0 }
};

// Card selection animation variants
const cardVariants = {
  initial: { 
    scale: 0.95, 
    opacity: 0,
    backgroundColor: '#FFFFFF',
    borderColor: '#E5E7EB',
    transition: {
      duration: 0.2
    }
  },
  animate: { 
    scale: 1, 
    opacity: 1,
    backgroundColor: '#FFFFFF',
    borderColor: '#E5E7EB',
    transition: {
      duration: 0.2
    }
  },
  hover: { 
    scale: 1.02,
    transition: { 
      duration: 0.2 
    } 
  },
  tap: { 
    scale: 0.98,
    transition: {
      duration: 0.1
    }
  },
  selected: { 
    scale: 1,
    backgroundColor: '#EEF2FF',
    borderColor: '#6366F1',
    opacity: 1,
    transition: { 
      duration: 0.2 
    }
  }
};

const STEPS = [
  {
    id: 'personal-info',
    title: "Personal Information",
    subtitle: "Tell us about yourself and your business",
    icon: UserIcon
  },
  {
    id: 'business-details',
    title: "Business Details",
    subtitle: "Help us understand your business structure",
    icon: BuildingOffice2Icon
  },
  {
    id: 'financial-info',
    title: "Financial Information",
    subtitle: "Provide your business financial overview",
    icon: CurrencyDollarIcon
  },
  {
    id: 'risk-assessment',
    title: "Risk Assessment",
    subtitle: "Complete your audit protection profile",
    icon: ShieldExclamationIcon
  }
];

const BusinessTypeCard = ({ type, icon: Icon, description, selected, onClick }) => (
  <motion.div
    variants={cardVariants}
    initial="initial"
    animate={selected ? "selected" : "animate"}
    whileHover="hover"
    whileTap="tap"
    onClick={onClick}
    className={`cursor-pointer rounded-xl border-2 p-6 flex flex-col items-center justify-center space-y-3 ${
      selected ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
    }`}
  >
    <Icon className={`h-10 w-10 ${selected ? 'text-indigo-600' : 'text-gray-600'}`} />
    <div className="text-center">
      <span className="font-semibold text-gray-900 text-sm">{type}</span>
      <p className="text-xs text-gray-500 mt-1">{description}</p>
    </div>
  </motion.div>
);

const ProgressBar = ({ currentStep, totalSteps }) => (
  <div className="fixed bottom-0 left-0 w-full h-1 bg-gray-200">
    <motion.div
      className="h-full bg-indigo-600"
      initial={{ width: 0 }}
      animate={{ 
        width: `${((currentStep + 1) / totalSteps) * 100}%`,
        transition: { duration: 0.5 }
      }}
    />
  </div>
);

const IntakeForm = () => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    // Personal Info (Required)
    firstName: '',
    lastName: '',
    email: '',  // This will be used for registration
    phone: '',
    
    // Personal Info (Optional)
    personalEmail: '',
    businessAddress: '',
    city: '',
    state: '',
    zipCode: '',
    website: '',
    socialSecurityNumber: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    
    // Business Info (Required)
    companyName: '',
    businessType: '',  // Keep as string for backward compatibility
    businessTypes: [],  // New field for multiple selections
    
    // Business Info (Optional)
    yearsInBusiness: 1,
    numberOfLocations: 1,
    employeeCount: 1,
    businessDescription: '',
    federalTaxId: '',
    stateTaxId: '',
    
    // Financial Info (Required)
    annualRevenue: 120000,
    taxPreparationMethod: '',
    
    // Financial Info (Optional)
    monthlyIncome: 10000,
    hasAccountant: false,
    accountantName: '',
    accountantPhone: '',
    accountantEmail: '',
    quarterlyEstimatedPayments: false,
    previousYearRevenue: '',
    
    // Risk Factors (Required)
    industryType: '',
    mainConcern: '',
    
    // Risk Factors (Optional)
    hasDigitalAssets: false,
    hasPreviousAudit: false,
    previousAuditYear: '',
    previousAuditOutcome: '',
    hasForeignAssets: false,
    hasCryptocurrency: false,
    hasRealEstate: false,
    hasBusinessPartners: false,
    
    // Additional Info (Optional)
    referralCode: '',
    additionalNotes: '',
    howDidYouHear: '',
    preferredContactMethod: 'email',
    marketingConsent: false,
    privacyConsent: false
  });

  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    return phoneRegex.test(cleanPhone) && cleanPhone.length >= 10;
  };

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      // Send all form data to backend
      const res = await api.post('/api/intake', formData);
      
      if (res.data.success) {
          localStorage.setItem('intake', JSON.stringify(res.data));
          navigate('/register', { state: { intake: res.data } });
      }
    } catch (err) {
      console.error('Error submitting intake form:', err);
      // Show error to user
      toast.error(err.response?.data?.error || 'An error occurred while submitting the form. Please try again.');
    }
  };

  const renderPersonalInfoStep = () => (
          <motion.div
      className="space-y-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Required Information Section */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-100">
        <div className="flex items-center mb-4">
          <LockClosedIcon className="h-5 w-5 text-indigo-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Required Information</h3>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          This information is required to create your IRS audit protection account and ensure compliance.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name *
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => updateFormData('firstName', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your first name"
            required
          />
            </div>
          </div>
          
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name *
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => updateFormData('lastName', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your last name"
            required
          />
        </div>
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Email Address *
              <span className="text-xs text-gray-500 ml-1">(Used for account login)</span>
          </label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="email"
            value={formData.email}
            onChange={(e) => updateFormData('email', e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 ${
                  formData.email && !validateEmail(formData.email) 
                    ? 'border-red-300 bg-red-50' 
                    : formData.email && validateEmail(formData.email)
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-300'
                }`}
                placeholder="business@company.com"
            required
          />
              {formData.email && validateEmail(formData.email) && (
                <CheckCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
              )}
              {formData.email && !validateEmail(formData.email) && (
                <ExclamationTriangleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
              )}
            </div>
            {formData.email && !validateEmail(formData.email) && (
              <p className="text-red-500 text-xs mt-1">Please enter a valid email address</p>
            )}
          </div>
          
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number *
              <span className="text-xs text-gray-500 ml-1">(Primary contact)</span>
            </label>
            <div className="relative">
              <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="tel"
            value={formData.phone}
                onChange={(e) => updateFormData('phone', formatPhoneNumber(e.target.value))}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 ${
                  formData.phone && !validatePhone(formData.phone) 
                    ? 'border-red-300 bg-red-50' 
                    : formData.phone && validatePhone(formData.phone)
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-300'
                }`}
            placeholder="(555) 123-4567"
            required
          />
              {formData.phone && validatePhone(formData.phone) && (
                <CheckCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
              )}
              {formData.phone && !validatePhone(formData.phone) && (
                <ExclamationTriangleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
              )}
            </div>
            {formData.phone && !validatePhone(formData.phone) && (
              <p className="text-red-500 text-xs mt-1">Please enter a valid phone number</p>
            )}
          </div>
        </div>
      </div>

      {/* Optional Information Section */}
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="flex items-center mb-4">
          <QuestionMarkCircleIcon className="h-5 w-5 text-gray-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Additional Information (Optional)</h3>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Providing additional information helps us better assess your audit risk and customize your protection plan.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Personal Email
              <span className="text-xs text-gray-500 ml-1">(If different from business email)</span>
            </label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                value={formData.personalEmail}
                onChange={(e) => updateFormData('personalEmail', e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 ${
                  formData.personalEmail && !validateEmail(formData.personalEmail) 
                    ? 'border-red-300 bg-red-50' 
                    : 'border-gray-300'
                }`}
                placeholder="personal@email.com"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Website
            </label>
            <div className="relative">
              <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="url"
                value={formData.website}
                onChange={(e) => updateFormData('website', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                placeholder="https://yourcompany.com"
              />
            </div>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Address
            </label>
            <div className="relative">
              <MapPinIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={formData.businessAddress}
                onChange={(e) => updateFormData('businessAddress', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                placeholder="123 Business Street, Suite 100"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => updateFormData('city', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
              placeholder="City"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State
            </label>
            <select
              value={formData.state}
              onChange={(e) => updateFormData('state', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select State</option>
              <option value="AL">Alabama</option>
              <option value="AK">Alaska</option>
              <option value="AZ">Arizona</option>
              <option value="AR">Arkansas</option>
              <option value="CA">California</option>
              <option value="CO">Colorado</option>
              <option value="CT">Connecticut</option>
              <option value="DE">Delaware</option>
              <option value="FL">Florida</option>
              <option value="GA">Georgia</option>
              <option value="HI">Hawaii</option>
              <option value="ID">Idaho</option>
              <option value="IL">Illinois</option>
              <option value="IN">Indiana</option>
              <option value="IA">Iowa</option>
              <option value="KS">Kansas</option>
              <option value="KY">Kentucky</option>
              <option value="LA">Louisiana</option>
              <option value="ME">Maine</option>
              <option value="MD">Maryland</option>
              <option value="MA">Massachusetts</option>
              <option value="MI">Michigan</option>
              <option value="MN">Minnesota</option>
              <option value="MS">Mississippi</option>
              <option value="MO">Missouri</option>
              <option value="MT">Montana</option>
              <option value="NE">Nebraska</option>
              <option value="NV">Nevada</option>
              <option value="NH">New Hampshire</option>
              <option value="NJ">New Jersey</option>
              <option value="NM">New Mexico</option>
              <option value="NY">New York</option>
              <option value="NC">North Carolina</option>
              <option value="ND">North Dakota</option>
              <option value="OH">Ohio</option>
              <option value="OK">Oklahoma</option>
              <option value="OR">Oregon</option>
              <option value="PA">Pennsylvania</option>
              <option value="RI">Rhode Island</option>
              <option value="SC">South Carolina</option>
              <option value="SD">South Dakota</option>
              <option value="TN">Tennessee</option>
              <option value="TX">Texas</option>
              <option value="UT">Utah</option>
              <option value="VT">Vermont</option>
              <option value="VA">Virginia</option>
              <option value="WA">Washington</option>
              <option value="WV">West Virginia</option>
              <option value="WI">Wisconsin</option>
              <option value="WY">Wyoming</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ZIP Code
            </label>
            <input
              type="text"
              value={formData.zipCode}
              onChange={(e) => updateFormData('zipCode', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
              placeholder="12345"
            />
          </div>
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-start">
          <LockClosedIcon className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Your Privacy & Security</h4>
            <p className="text-sm text-blue-800 mb-3">
              All information provided is encrypted and stored securely. We use bank-level security to protect your sensitive financial data and comply with all federal privacy regulations.
            </p>
            <div className="flex items-start space-x-3">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  id="privacyConsent"
                  checked={formData.privacyConsent}
                  onChange={(e) => updateFormData('privacyConsent', e.target.checked)}
                  className="sr-only"
                />
                <div 
                  onClick={() => updateFormData('privacyConsent', !formData.privacyConsent)}
                  className={`w-5 h-5 border-2 rounded flex items-center justify-center cursor-pointer transition-all duration-200 ${
                    formData.privacyConsent 
                      ? 'bg-indigo-600 border-indigo-600' 
                      : 'bg-white border-gray-300 hover:border-indigo-400'
                  }`}
                >
                  {formData.privacyConsent && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
              <label 
                htmlFor="privacyConsent" 
                className="text-sm text-blue-800 cursor-pointer leading-relaxed"
              >
                I understand and consent to the secure handling of my personal and business information
              </label>
            </div>
          </div>
        </div>
            </div>
          </motion.div>
        );

  const renderBusinessDetailsStep = () => {
    const businessTypes = [
      { type: 'Sole Proprietor', icon: UserIcon, description: 'Individual business owner' },
      { type: 'LLC', icon: BuildingOffice2Icon, description: 'Limited Liability Company' },
      { type: 'S-Corp', icon: BriefcaseIcon, description: 'S Corporation' },
      { type: 'C-Corp', icon: BuildingOffice2Icon, description: 'C Corporation' },
      { type: 'Partnership', icon: UsersIcon, description: 'General Partnership' },
      { type: 'LLP', icon: BriefcaseIcon, description: 'Limited Liability Partnership' },
      { type: '1099 Contractor', icon: DocumentTextIcon, description: 'Independent contractor' },
      { type: 'Non-Profit', icon: CubeIcon, description: 'Tax-exempt organization' }
    ];

    const toggleBusinessType = (type) => {
      updateFormData('businessTypes', 
        formData.businessTypes.includes(type)
          ? formData.businessTypes.filter(t => t !== type)
          : [type]  // Replace with new selection instead of adding
      );
      // Keep the last selection in businessType for backward compatibility
      updateFormData('businessType', type);
    };

        return (
          <motion.div
        className="space-y-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
          >
        {/* Required Business Information */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-100">
          <div className="flex items-center mb-4">
            <BuildingOffice2Icon className="h-5 w-5 text-indigo-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Required Business Information</h3>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            This information helps us understand your business structure and assess audit risk.
          </p>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name *
                <span className="text-xs text-gray-500 ml-1">(Legal business name)</span>
              </label>
              <div className="relative">
                <BuildingOffice2Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={formData.companyName}
            onChange={(e) => updateFormData('companyName', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your business name"
            required
          />
              </div>
        </div>

        <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Structure *
                <span className="text-xs text-gray-500 ml-1">(Choose your business type)</span>
          </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {businessTypes.map((item) => (
              <BusinessTypeCard
                key={item.type}
                type={item.type}
                icon={item.icon}
                    description={item.description}
                selected={formData.businessTypes.includes(item.type)}
                onClick={() => toggleBusinessType(item.type)}
              />
            ))}
              </div>
            </div>
          </div>
        </div>

        {/* Optional Business Details */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
          <div className="flex items-center mb-4">
            <QuestionMarkCircleIcon className="h-5 w-5 text-gray-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Additional Business Details (Optional)</h3>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Providing additional details helps us create a more comprehensive audit protection plan.
          </p>
          
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
              Years in Business: {formData.yearsInBusiness}
                  <span className="text-xs text-gray-500 ml-1">(Business experience)</span>
            </label>
            <input
              type="range"
              min="1"
              max="50"
              value={formData.yearsInBusiness}
              onChange={(e) => updateFormData('yearsInBusiness', parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 year</span>
                  <span>50+ years</span>
            </div>
              </div>
              
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Locations: {formData.numberOfLocations}
              <span className="text-xs text-gray-500 ml-1">(Business scale)</span>
            </label>
            <input
              type="range"
              min="1"
              max="20"
              value={formData.numberOfLocations}
              onChange={(e) => updateFormData('numberOfLocations', parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 location</span>
                  <span>20+ locations</span>
                </div>
          </div>
        </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Employees
              <span className="text-xs text-gray-500 ml-1">(Full & part-time)</span>
            </label>
            <input
              type="number"
              min="1"
              value={formData.employeeCount}
              onChange={(e) => updateFormData('employeeCount', Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  placeholder="Number of employees"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Federal Tax ID (EIN)
                  <span className="text-xs text-gray-500 ml-1">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.federalTaxId}
                  onChange={(e) => updateFormData('federalTaxId', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  placeholder="XX-XXXXXXX"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Description
                <span className="text-xs text-gray-500 ml-1">(What does your business do?)</span>
              </label>
              <textarea
                value={formData.businessDescription}
                onChange={(e) => updateFormData('businessDescription', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                rows={3}
                placeholder="Briefly describe your business activities..."
              />
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderFinancialInfoStep = () => (
    <motion.div 
      className="space-y-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Required Financial Information */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-100">
        <div className="flex items-center mb-4">
          <CurrencyDollarIcon className="h-5 w-5 text-indigo-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Required Financial Information</h3>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          This information helps us assess your audit risk and determine the appropriate protection level.
        </p>
        
        <div className="space-y-6">
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
            Annual Gross Revenue: ${formData.annualRevenue.toLocaleString()}
            <span className="text-xs text-gray-500 ml-1">(Total revenue before expenses)</span>
          </label>
          <input
            type="range"
            min="50000"
            max="10000000"
            step="50000"
            value={formData.annualRevenue}
            onChange={(e) => updateFormData('annualRevenue', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>$50K</span>
              <span>$10M+</span>
        </div>
          </div>

        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tax Preparation Method *
              <span className="text-xs text-gray-500 ml-1">(How do you prepare your taxes?)</span>
            </label>
            <select
              value={formData.taxPreparationMethod}
              onChange={(e) => updateFormData('taxPreparationMethod', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select method</option>
              <option value="self">Self-prepared</option>
              <option value="accountant">Professional Accountant</option>
              <option value="software">Tax Software (TurboTax, H&R Block, etc.)</option>
              <option value="tax-preparer">Tax Preparer</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {/* Optional Financial Details */}
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="flex items-center mb-4">
          <QuestionMarkCircleIcon className="h-5 w-5 text-gray-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Additional Financial Details (Optional)</h3>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Providing additional financial information helps us create a more comprehensive audit protection plan.
        </p>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
            Annual Net Revenue: ${formData.monthlyIncome.toLocaleString()}
            <span className="text-xs text-gray-500 ml-1">(Revenue after expenses)</span>
          </label>
          <input
            type="range"
            min="25000"
            max="5000000"
            step="25000"
            value={formData.monthlyIncome}
            onChange={(e) => updateFormData('monthlyIncome', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>$25K</span>
              <span>$5M+</span>
        </div>
      </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Do you have an accountant?
              </label>
          <div className="flex space-x-4">
            {['Yes', 'No'].map((option) => (
              <button
                key={option}
                onClick={() => updateFormData('hasAccountant', option === 'Yes')}
                    className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                  formData.hasAccountant === (option === 'Yes')
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {option}
              </button>
                  ))}
                </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Do you make quarterly estimated payments?
              </label>
              <div className="flex space-x-4">
                {['Yes', 'No'].map((option) => (
                  <button
                    key={option}
                    onClick={() => updateFormData('quarterlyEstimatedPayments', option === 'Yes')}
                    className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                      formData.quarterlyEstimatedPayments === (option === 'Yes')
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {formData.hasAccountant && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-900 mb-3">Accountant Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Accountant Name
                  </label>
                  <input
                    type="text"
                    value={formData.accountantName}
                    onChange={(e) => updateFormData('accountantName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Accountant or firm name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Accountant Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.accountantPhone}
                    onChange={(e) => updateFormData('accountantPhone', formatPhoneNumber(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Accountant Email
                  </label>
                  <input
                    type="email"
                    value={formData.accountantEmail}
                    onChange={(e) => updateFormData('accountantEmail', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="accountant@firm.com"
                  />
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Previous Year Revenue
              <span className="text-xs text-gray-500 ml-1">(For comparison)</span>
            </label>
            <input
              type="number"
              value={formData.previousYearRevenue}
              onChange={(e) => updateFormData('previousYearRevenue', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
              placeholder="Enter previous year revenue"
            />
          </div>
        </div>
            </div>
          </motion.div>
        );

  const renderRiskAssessmentStep = () => (
          <motion.div
      className="space-y-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
          >
      {/* Required Risk Assessment */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-100">
        <div className="flex items-center mb-4">
          <ShieldExclamationIcon className="h-5 w-5 text-indigo-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Required Risk Assessment</h3>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          This information helps us understand your specific audit risks and customize your protection plan.
        </p>
        
        <div className="space-y-6">
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Industry Type *
              <span className="text-xs text-gray-500 ml-1">(Your business industry)</span>
            </label>
            <select
              value={formData.industryType}
              onChange={(e) => updateFormData('industryType', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select industry</option>
              <option value="retail">Retail & E-commerce</option>
              <option value="technology">Technology & Software</option>
              <option value="healthcare">Healthcare & Medical</option>
              <option value="construction">Construction & Contracting</option>
              <option value="hospitality">Hospitality & Food Service</option>
              <option value="professional">Professional Services</option>
              <option value="manufacturing">Manufacturing</option>
              <option value="real-estate">Real Estate</option>
              <option value="finance">Finance & Insurance</option>
              <option value="transportation">Transportation & Logistics</option>
              <option value="education">Education & Training</option>
              <option value="agriculture">Agriculture & Farming</option>
              <option value="energy">Energy & Utilities</option>
              <option value="media">Media & Entertainment</option>
              <option value="consulting">Consulting</option>
              <option value="legal">Legal Services</option>
              <option value="nonprofit">Non-Profit</option>
              <option value="automotive">Automotive & Repair</option>
              <option value="beauty">Beauty & Personal Care</option>
              <option value="fitness">Fitness & Wellness</option>
              <option value="restaurant">Restaurant & Food Service</option>
              <option value="cleaning">Cleaning & Maintenance</option>
              <option value="landscaping">Landscaping & Gardening</option>
              <option value="photography">Photography & Videography</option>
              <option value="marketing">Marketing & Advertising</option>
              <option value="design">Design & Creative Services</option>
              <option value="security">Security & Protection</option>
              <option value="logistics">Logistics & Warehousing</option>
              <option value="import-export">Import/Export</option>
              <option value="mining">Mining & Extraction</option>
              <option value="telecommunications">Telecommunications</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Main Audit Concern *
              <span className="text-xs text-gray-500 ml-1">(What worries you most about audits?)</span>
            </label>
            <select
              value={formData.mainConcern}
              onChange={(e) => updateFormData('mainConcern', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select concern</option>
              <option value="accuracy">Accuracy of tax filings</option>
              <option value="history">Previous audit history</option>
              <option value="complexity">Complex business transactions</option>
              <option value="industry">Industry-specific risks</option>
              <option value="deductions">Business expense deductions</option>
              <option value="income">Income reporting accuracy</option>
              <option value="general">General peace of mind</option>
            </select>
          </div>
        </div>
      </div>

      {/* Optional Risk Factors */}
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="flex items-center mb-4">
          <QuestionMarkCircleIcon className="h-5 w-5 text-gray-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Additional Risk Factors (Optional)</h3>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          These factors help us assess your specific audit risk profile.
        </p>
        
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Do you deal with digital assets?
              </label>
          <div className="flex space-x-4">
            {['Yes', 'No'].map((option) => (
              <button
                key={option}
                onClick={() => updateFormData('hasDigitalAssets', option === 'Yes')}
                    className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                  formData.hasDigitalAssets === (option === 'Yes')
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Previous audit history?
              </label>
          <div className="flex space-x-4">
            {['Yes', 'No'].map((option) => (
              <button
                key={option}
                onClick={() => updateFormData('hasPreviousAudit', option === 'Yes')}
                    className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                  formData.hasPreviousAudit === (option === 'Yes')
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Do you have cryptocurrency?
              </label>
              <div className="flex space-x-4">
                {['Yes', 'No'].map((option) => (
                  <button
                    key={option}
                    onClick={() => updateFormData('hasCryptocurrency', option === 'Yes')}
                    className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                      formData.hasCryptocurrency === (option === 'Yes')
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
      </div>

      <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Do you have real estate investments?
              </label>
              <div className="flex space-x-4">
                {['Yes', 'No'].map((option) => (
                  <button
                    key={option}
                    onClick={() => updateFormData('hasRealEstate', option === 'Yes')}
                    className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                      formData.hasRealEstate === (option === 'Yes')
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Do you have foreign assets?
              </label>
              <div className="flex space-x-4">
                {['Yes', 'No'].map((option) => (
                  <button
                    key={option}
                    onClick={() => updateFormData('hasForeignAssets', option === 'Yes')}
                    className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                      formData.hasForeignAssets === (option === 'Yes')
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Do you have business partners?
              </label>
              <div className="flex space-x-4">
                {['Yes', 'No'].map((option) => (
                  <button
                    key={option}
                    onClick={() => updateFormData('hasBusinessPartners', option === 'Yes')}
                    className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                      formData.hasBusinessPartners === (option === 'Yes')
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {formData.hasPreviousAudit && (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <h4 className="text-sm font-semibold text-yellow-900 mb-3">Previous Audit Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Audit Year
                  </label>
                  <input
                    type="text"
                    value={formData.previousAuditYear}
                    onChange={(e) => updateFormData('previousAuditYear', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="e.g., 2022"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Audit Outcome
                  </label>
        <select
                    value={formData.previousAuditOutcome}
                    onChange={(e) => updateFormData('previousAuditOutcome', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Select outcome</option>
                    <option value="no-change">No change</option>
                    <option value="additional-tax">Additional tax owed</option>
                    <option value="refund">Refund received</option>
                    <option value="penalty">Penalty assessed</option>
                    <option value="other">Other</option>
        </select>
      </div>
              </div>
            </div>
          )}

      <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes
              <span className="text-xs text-gray-500 ml-1">(Any specific concerns or questions)</span>
            </label>
        <textarea
          value={formData.additionalNotes}
          onChange={(e) => updateFormData('additionalNotes', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
              rows={4}
              placeholder="Please share any specific concerns, questions, or additional information that might be relevant to your audit protection needs..."
            />
          </div>
        </div>
            </div>
          </motion.div>
        );

  const renderStepContent = () => {
    switch (STEPS[step].id) {
      case 'personal-info':
        return renderPersonalInfoStep();
      case 'business-details':
        return renderBusinessDetailsStep();
      case 'financial-info':
        return renderFinancialInfoStep();
      case 'risk-assessment':
        return renderRiskAssessmentStep();
      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (STEPS[step].id) {
      case 'personal-info':
        return formData.firstName && formData.lastName && formData.email && formData.phone && 
               validateEmail(formData.email) && validatePhone(formData.phone) && formData.privacyConsent;
      case 'business-details':
        return formData.companyName && formData.businessTypes.length > 0;
      case 'financial-info':
        return formData.annualRevenue > 0 && formData.taxPreparationMethod;
      case 'risk-assessment':
        return formData.industryType && formData.mainConcern;
      default:
        return false;
    }
  };

        return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 py-16">
        <div className="mb-6">
          <button
            onClick={() => window.history.length > 1 ? window.history.back() : window.location.assign('/')}
            className="inline-flex items-center text-indigo-600 hover:text-indigo-800"
          >
            <ChevronLeftIcon className="h-5 w-5 mr-1" />
            Back to previous page
          </button>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="space-y-8"
          >
            {/* Header */}
            <div className="text-center space-y-2">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {React.createElement(STEPS[step].icon, {
                  className: "h-12 w-12 mx-auto text-indigo-600"
                })}
              </motion.div>
              <motion.h1
                className="text-3xl font-bold text-gray-900"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {STEPS[step].title}
              </motion.h1>
              <motion.p
                className="text-gray-500"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                {STEPS[step].subtitle}
              </motion.p>
            </div>

            {/* Form Content */}
            <div className="mt-8">
              {renderStepContent()}
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-8">
              <button
                onClick={handleBack}
                disabled={step === 0}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                  step === 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <ChevronLeftIcon className="h-5 w-5" />
                <span>Back</span>
              </button>
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg ${
                  canProceed()
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                  <span>{step === STEPS.length - 1 ? "Complete Registration" : "Continue"}</span>
  <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Progress Bar */}
      <ProgressBar currentStep={step} totalSteps={STEPS.length} />
    </div>
  );
};

export default IntakeForm; 